/* OVD search query expansion — shared by the header search modal and the
   /search page so the preview and the results page always agree.

   Shopify's /search matches product descriptions, which makes "wallet" return
   jackets. We scope every word to title / tag / product_type / vendor instead.
   Field-scoped searches do NOT support prefix matching though ("singul" never
   finds "SINGULARITIES ARE REAL"), so we complete each typed prefix against a
   word index of the catalogue first (/collections/all?view=ovd-search-index)
   and send the real words. Words are ANDed, fields and completions are ORed. */
(function () {
  if (window.OvdSearchQuery) return;

  var FIELDS = ['title', 'tag', 'product_type', 'vendor'];
  var INDEX_URL = '/collections/all?view=ovd-search-index';
  var CACHE_KEY = 'ovd_search_index_v2';
  var CACHE_TTL = 6 * 60 * 60 * 1000; /* 6h — sessionStorage, so at most one tab session anyway */
  var MAX_PER_WORD = 10;               /* completions per typed word, most common first */
  var MAX_FUZZY = 6;                   /* typo-corrected completions per typed word */
  var MAX_PAGES = 8;                   /* 8 × 250 products */
  var RETRY_COOLDOWN = 30 * 1000;      /* after a failed index fetch, retry at most every 30s */
  /* Shopify drops these inside scoped queries — "title:or" matches nothing, so
     a typed "wings or death" must become wings AND death. */
  var STOP = {};
  ('a an and are as at be but by for if in into is it no not of on or such that the ' +
   'their then there these they this to was will with & + - / | vs').split(' ')
    .forEach(function (w) { STOP[w] = 1; });

  var tokens = null;      /* { word: productCount } */
  var loading = null;     /* Promise while the index is fetching */
  var failedAt = 0;       /* time of the last failed index fetch */

  function stripEdges(w) { return w.replace(/^[^a-z0-9$]+|[^a-z0-9]+$/g, ''); }

  function buildTokens(items) {
    var counts = {};
    items.forEach(function (s) {
      var seen = {};
      String(s || '').toLowerCase().split(/\s+/).forEach(function (raw) {
        var w = stripEdges(raw);
        if (!w) return;
        seen[w] = 1;
        /* "2-tone", "black/white", "l/s" → also index the parts */
        w.split(/[-\/]+/).forEach(function (part) { if (part && part !== w) seen[part] = 1; });
      });
      Object.keys(seen).forEach(function (w) { counts[w] = (counts[w] || 0) + 1; });
    });
    return counts;
  }

  function fetchPage(p) {
    /* Accept must be text/html: asking Shopify for application/json on a
       collection URL returns the collection's JSON endpoint, not the template. */
    return fetch(INDEX_URL + '&page=' + p, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('index ' + r.status); return r.text(); })
      .then(function (t) { return JSON.parse(t); });
  }

  function readCache() {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (c && c.t && typeof c.t === 'object' && Object.keys(c.t).length && (Date.now() - (c.at || 0)) < CACHE_TTL) return c.t;
    } catch (e) {}
    return null;
  }

  function load() {
    if (tokens) return Promise.resolve(tokens);
    var cached = readCache();
    if (cached) { tokens = cached; return Promise.resolve(tokens); }
    if (loading) return loading;
    if (failedAt && (Date.now() - failedAt) < RETRY_COOLDOWN) return Promise.resolve(null);
    loading = fetchPage(1).then(function (first) {
      var rest = [];
      var pages = Math.min(first.pages || 1, MAX_PAGES);
      for (var p = 2; p <= pages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (more) {
        var items = (first.items || []).slice();
        more.forEach(function (pg) { items = items.concat(pg.items || []); });
        var built = buildTokens(items);
        if (!Object.keys(built).length) throw new Error('index empty');
        tokens = built;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), t: tokens })); } catch (e) {}
        return tokens;
      });
    }).catch(function (err) {
      /* No index → fall back to sending the typed words as-is (exact matches only). */
      if (window.console && console.warn) console.warn('[ovd-search] word index unavailable:', err);
      loading = null;
      failedAt = Date.now();
      return null;
    });
    return loading;
  }

  /* Characters that would break Shopify's query grammar. */
  function clean(w) { return String(w || '').replace(/[():"]/g, ''); }

  /* Optimal-string-alignment edit distance (insert / delete / substitute /
     adjacent swap), capped so we can bail out early. */
  function editDistance(a, b, cap) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > cap) return cap + 1;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > cap) return cap + 1;
      prev2 = prev; prev = cur;
    }
    return prev[lb];
  }

  /* Typo tolerance: Shopify's plain search forgives "singularties", scoped
     queries don't — so find index words within a small edit distance of the
     typed word, or of the typed word as a prefix ("singularti…"). */
  function fuzzy(w, toks) {
    var cap = w.length >= 8 ? 2 : (w.length >= 4 ? 1 : 0);
    if (!cap) return [];
    var scored = [];
    Object.keys(toks).forEach(function (t) {
      if (t.length < w.length - cap) return;
      var d = editDistance(w, t, cap);
      if (d > cap && t.length > w.length) {
        /* prefix with a typo: compare against the token's head of similar
           length; ranks just below a whole-word correction */
        for (var L = w.length - 1; L <= w.length + 1 && d > cap; L++) {
          if (L > 0 && L < t.length) d = Math.min(d, editDistance(w, t.slice(0, L), cap) + 0.5);
        }
      }
      if (d <= cap + 0.5) scored.push({ t: t, d: d, n: toks[t] });
    });
    scored.sort(function (a, b) { return (a.d - b.d) || (b.n - a.n) || (a.t.length - b.t.length) || (a.t < b.t ? -1 : 1); });
    return scored.slice(0, MAX_FUZZY).map(function (x) { return x.t; });
  }

  function completions(word, toks) {
    var w = String(word || '').toLowerCase();
    if (!w) return [];
    if (!toks) return [clean(w)].filter(Boolean);
    var out = [];
    if (Object.prototype.hasOwnProperty.call(toks, w)) out.push(w);
    var pre = Object.keys(toks).filter(function (t) { return t !== w && t.indexOf(w) === 0; });
    pre.sort(function (a, b) { return (toks[b] - toks[a]) || (a.length - b.length) || (a < b ? -1 : 1); });
    out = out.concat(pre).slice(0, MAX_PER_WORD);
    if (!out.length) out = fuzzy(w, toks);
    if (!out.length) out = [w];
    var uniq = [];
    out.map(clean).forEach(function (c) { if (c && uniq.indexOf(c) < 0) uniq.push(c); });
    return uniq;
  }

  function expandWith(raw, toks) {
    var typed = String(raw || '').trim().split(/\s+/).filter(Boolean);
    if (!typed.length) return '';
    /* "t-shirt" with no such word in the index → search "t" AND "shirt" */
    var words = [];
    typed.forEach(function (w) {
      var lw = w.toLowerCase();
      if (/[-\/]/.test(lw) && toks && !Object.prototype.hasOwnProperty.call(toks, lw)) {
        lw.split(/[-\/]+/).forEach(function (p) { if (p) words.push(p); });
      } else {
        words.push(lw);
      }
    });
    var groups = [];
    words.forEach(function (w) {
      if (STOP[w] || !/[a-z0-9]/.test(w)) return;   /* "wings or death" → wings AND death */
      var cs = completions(w, toks);
      if (!cs.length) return;
      var clauses = [];
      FIELDS.forEach(function (f) { cs.forEach(function (c) { clauses.push(f + ':' + c); }); });
      groups.push('(' + clauses.join(' OR ') + ')');
    });
    return groups.join(' AND ');
  }

  /* Promise<string> — waits for the word index (cached after the first call). */
  function expand(raw) { return load().then(function (toks) { return expandWith(raw, toks); }); }

  /* string — uses the index if it is already loaded, else the typed words. */
  function expandSync(raw) { return expandWith(raw, tokens); }

  /* Best-effort recovery of the typed words from an expanded query (old links
     without the ovd_q param). */
  function plain(expanded) {
    var s = String(expanded || '');
    if (!/\b(title|tag|product_type|vendor):/.test(s)) return s;
    var words = [];
    if (/\)\s*AND\s*\(/.test(s)) {
      /* new format: one group per typed word, completions ORed inside */
      s.split(/\)\s*AND\s*\(/).forEach(function (g) {
        var m = g.match(/title:([^\s()]+)/);
        if (m && words.indexOf(m[1]) < 0) words.push(m[1]);
      });
    } else {
      /* old format: (title:a title:b) OR (tag:a tag:b) … */
      var first = s.split(/\)\s*OR\s*\(/)[0];
      (first.match(/title:([^\s()]+)/g) || []).forEach(function (x) {
        var w = x.slice(6); if (words.indexOf(w) < 0) words.push(w);
      });
    }
    return words.join(' ');
  }

  /* Build the /search URL the way both the modal and the page do. */
  function pageUrl(expanded, typed) {
    var p = new URLSearchParams();
    p.set('q', expanded);
    p.set('type', 'product');
    p.set('options[prefix]', 'last');
    p.set('ovd_q', typed);
    return '/search?' + p.toString();
  }

  window.OvdSearchQuery = {
    load: load,
    expand: expand,
    expandSync: expandSync,
    plain: plain,
    pageUrl: pageUrl,
    FIELDS: FIELDS
  };
})();
